import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppInventory } from "@/hooks/useAppInventory";
import { useApplications } from "@/hooks/useApplications";
import { useAppCveMatches } from "@/hooks/useAppCveMatches";
import { GlassCard, GlassCardContent } from "@/components/ui/glass-card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Package, ShieldCheck, ShieldAlert, Search, Info, Download, FileText, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { isAppleApp, isMatchableApp, AggregatedAppCveResult } from "@/services/appCveMatcher";
import { NvdCveData } from "@/services/nvdApi";
import { AppCveDetailSheet } from "./AppCveDetailSheet";
import {
  buildAppCveCsv,
  downloadCsv,
  generateAppCvePdf,
  downloadAppCvePdf,
} from "@/utils/appCveExport";

type SeverityFilter = "all" | "vulnerable" | "critical" | "high" | "medium" | "low" | "clean";

const SEVERITY_BADGE: Record<NvdCveData["severity"], string> = {
  CRITICAL: "bg-destructive text-destructive-foreground",
  HIGH: "bg-destructive/80 text-destructive-foreground",
  MEDIUM: "bg-warning text-warning-foreground",
  LOW: "bg-muted text-muted-foreground",
  NONE: "bg-muted text-muted-foreground",
};

const SEVERITY_RANK: Record<NvdCveData["severity"], number> = {
  CRITICAL: 4,
  HIGH: 3,
  MEDIUM: 2,
  LOW: 1,
  NONE: 0,
};

export function ApplicationsVulnerabilityTab() {
  const navigate = useNavigate();
  const { inventory, isLoading } = useAppInventory();
  const { applications } = useApplications();
  const matches = useAppCveMatches(inventory);

  const catalogByBundle = useMemo(() => {
    const map = new Map<string, { id: string; name: string }>();
    for (const a of applications) {
      if (a.bundleId) map.set(a.bundleId.toLowerCase(), { id: a.id, name: a.name });
    }
    return map;
  }, [applications]);

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<SeverityFilter>("all");
  const [selected, setSelected] = useState<{ bundleId: string; name: string; versions: string[] } | null>(null);

  const summary = useMemo(() => {
    let vulnerable = 0;
    let critical = 0;
    let high = 0;
    matches.matchesByBundleId.forEach((m) => {
      if (m.matched && m.cves.length > 0) {
        vulnerable++;
        if (m.highestSeverity === "CRITICAL") critical++;
        if (m.highestSeverity === "HIGH") high++;
      }
    });
    return { vulnerable, critical, high };
  }, [matches.matchesByBundleId]);

  const rows = useMemo(() => {
    const lower = search.trim().toLowerCase();
    const filtered = inventory.filter((app) => {
      if (lower && !app.name.toLowerCase().includes(lower) && !app.bundle_id.toLowerCase().includes(lower)) {
        return false;
      }
      const m = matches.matchesByBundleId.get(app.bundle_id);
      const sev = m?.highestSeverity ?? "NONE";
      switch (filter) {
        case "vulnerable":
          return !!m && m.cves.length > 0;
        case "critical":
          return sev === "CRITICAL";
        case "high":
          return sev === "HIGH";
        case "medium":
          return sev === "MEDIUM";
        case "low":
          return sev === "LOW";
        case "clean":
          return !isAppleApp(app.bundle_id) && (!m || m.cves.length === 0);
        default:
          return true;
      }
    });
    return filtered.sort((a, b) => {
      const ma = matches.matchesByBundleId.get(a.bundle_id);
      const mb = matches.matchesByBundleId.get(b.bundle_id);
      const sa = SEVERITY_RANK[ma?.highestSeverity ?? "NONE"];
      const sb = SEVERITY_RANK[mb?.highestSeverity ?? "NONE"];
      if (sa !== sb) return sb - sa;
      return b.total_devices - a.total_devices;
    });
  }, [inventory, search, filter, matches.matchesByBundleId]);

  const filterButtons: { id: SeverityFilter; label: string }[] = [
    { id: "all", label: "All" },
    { id: "vulnerable", label: `Vulnerable (${summary.vulnerable})` },
    { id: "critical", label: `Critical (${summary.critical})` },
    { id: "high", label: `High (${summary.high})` },
    { id: "medium", label: "Medium" },
    { id: "low", label: "Low" },
    { id: "clean", label: "Clean" },
  ];

  const handleExport = async (format: "csv" | "pdf") => {
    if (rows.length === 0) return;
    const exportRows = rows.map((app) => ({
      app,
      match: matches.matchesByBundleId.get(app.bundle_id),
    }));
    const stamp = new Date().toISOString().slice(0, 10);
    try {
      if (format === "csv") {
        downloadCsv(`app-cve-report-${stamp}.csv`, buildAppCveCsv(exportRows));
        toast.success("CSV exported");
      } else {
        const blob = await generateAppCvePdf(exportRows, {
          totalApps: rows.length,
          vulnerable: summary.vulnerable,
          critical: summary.critical,
          high: summary.high,
        });
        downloadAppCvePdf(`app-cve-report-${stamp}.pdf`, blob);
        toast.success("PDF exported");
      }
    } catch (e) {
      toast.error("Export failed");
      console.error(e);
    }
  };

  const selectedMatch = selected ? matches.matchesByBundleId.get(selected.bundleId) : undefined;

  return (
    <TooltipProvider>
      <div className="space-y-4">
        <div className="grid gap-3 md:grid-cols-3">
          <SummaryCard
            label="Vulnerable apps"
            value={summary.vulnerable}
            icon={<ShieldAlert className="h-4 w-4 text-destructive" />}
            tone="destructive"
          />
          <SummaryCard
            label="Critical CVEs"
            value={summary.critical}
            icon={<ShieldAlert className="h-4 w-4 text-destructive" />}
            tone="destructive"
          />
          <SummaryCard
            label="High CVEs"
            value={summary.high}
            icon={<ShieldCheck className="h-4 w-4 text-warning" />}
            tone="warning"
          />
        </div>

        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Info className="h-3.5 w-3.5" />
          <span>
            CVE matches are based on public NVD CPE data — false positives are possible. Apple-managed apps
            are tracked via the OS feed.
          </span>
          {matches.isLoading && (
            <span className="ml-auto">
              Scanning {matches.loadedCount} / {matches.totalCount}…
            </span>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search apps or bundle IDs"
              className="pl-9"
            />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {filterButtons.map((b) => (
              <Button
                key={b.id}
                size="sm"
                variant={filter === b.id ? "default" : "outline"}
                onClick={() => setFilter(b.id)}
              >
                {b.label}
              </Button>
            ))}
          </div>
          <div className="sm:ml-auto">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline" disabled={rows.length === 0}>
                  <Download className="h-4 w-4 mr-1.5" />
                  Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleExport("csv")}>
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  Download CSV
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport("pdf")}>
                  <FileText className="h-4 w-4 mr-2" />
                  Download PDF report
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {isLoading ? (
          <div className="text-center py-12 text-sm text-muted-foreground">Loading app inventory…</div>
        ) : rows.length === 0 ? (
          <div className="text-center py-12 backdrop-blur-md bg-white/40 dark:bg-slate-900/40 border border-white/10 dark:border-white/5 rounded-2xl shadow-lg">
            <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No applications match your filters.</p>
          </div>
        ) : (
          <GlassCard variant="default">
            <GlassCardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Application</TableHead>
                    <TableHead className="text-muted-foreground">Bundle ID</TableHead>
                    <TableHead className="text-center">Version</TableHead>
                    <TableHead className="text-center">Devices</TableHead>
                    <TableHead className="text-center">CVE Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((app) => {
                    const allVersions = (app.versions ?? [])
                      .map((v) => v.version)
                      .filter(Boolean);
                    const versionDisplay =
                      allVersions.length === 0
                        ? "—"
                        : allVersions.length === 1
                          ? allVersions[0]
                          : `${allVersions[0]} +${allVersions.length - 1}`;
                    const apple = isAppleApp(app.bundle_id);
                    const matchable = allVersions.some((v) => isMatchableApp(app.bundle_id, v));
                    const m = matches.matchesByBundleId.get(app.bundle_id);
                    const progress = matches.progressByBundleId.get(app.bundle_id);
                    const isScanning = matchable && !!progress && !progress.done;

                    return (
                      <TableRow
                        key={app.bundle_id}
                        className={m && m.cves.length > 0 ? "cursor-pointer hover:bg-muted/40" : undefined}
                        onClick={() => {
                          if (m && m.cves.length > 0) {
                            setSelected({ bundleId: app.bundle_id, name: app.name, versions: allVersions });
                          }
                        }}
                      >
                        <TableCell className="font-medium">{app.name}</TableCell>
                        <TableCell className="text-xs text-muted-foreground font-mono">
                          {app.bundle_id}
                        </TableCell>
                        <TableCell className="text-center font-mono text-sm" title={allVersions.join(", ")}>
                          {versionDisplay}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="secondary">{app.total_devices}</Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <CveStatusCell
                            apple={apple}
                            matchable={matchable}
                            scanning={isScanning}
                            match={m}
                            progress={progress}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </GlassCardContent>
          </GlassCard>
        )}

        {selected && (() => {
          const catalogMatch = catalogByBundle.get(selected.bundleId.toLowerCase());
          const cveCount = selectedMatch?.cves.length ?? 0;
          const vulnerableVersions = selectedMatch?.vulnerableVersions ?? [];
          return (
            <AppCveDetailSheet
              open={!!selected}
              onOpenChange={(o) => !o && setSelected(null)}
              appName={selected.name}
              bundleId={selected.bundleId}
              versions={selected.versions}
              match={selectedMatch}
              remediationLabel={catalogMatch ? "Open in catalog to deploy update" : "Add to catalog & deploy"}
              remediationDescription={
                catalogMatch
                  ? `Opens "${catalogMatch.name}" in your application catalog where you can push the latest version to assigned devices.`
                  : `${selected.name} isn't in your catalog yet. We'll open the Add Application wizard prefilled with this app so you can publish and deploy it.`
              }
              onRemediate={() => {
                if (catalogMatch) {
                  toast.success(`Opening ${catalogMatch.name} in catalog`);
                  navigate(`/applications/${catalogMatch.id}`);
                } else {
                  toast.success(`Starting remediation for ${selected.name}`);
                  navigate("/applications/add", {
                    state: {
                      prefillSearch: selected.name,
                      remediationContext: {
                        appName: selected.name,
                        bundleId: selected.bundleId,
                        cveCount,
                        vulnerableVersions,
                      },
                    },
                  });
                }
                setSelected(null);
              }}
            />
          );
        })()}
      </div>
    </TooltipProvider>
  );
}

function SummaryCard({
  label,
  value,
  icon,
  tone,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
  tone: "destructive" | "warning" | "muted";
}) {
  const toneClass =
    tone === "destructive"
      ? "text-destructive"
      : tone === "warning"
        ? "text-warning"
        : "text-muted-foreground";
  return (
    <GlassCard variant="default">
      <GlassCardContent className="p-4 flex items-center justify-between">
        <div>
          <div className="text-xs text-muted-foreground">{label}</div>
          <div className={`text-2xl font-semibold ${toneClass}`}>{value}</div>
        </div>
        {icon}
      </GlassCardContent>
    </GlassCard>
  );
}

function CveStatusCell({
  apple,
  matchable,
  scanning,
  match,
  progress,
}: {
  apple: boolean;
  matchable: boolean;
  scanning: boolean;
  match?: AggregatedAppCveResult;
  progress?: { loaded: number; total: number; done: boolean };
}) {
  if (apple) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant="outline" className="text-[10px] text-muted-foreground">
            Tracked via OS feed
          </Badge>
        </TooltipTrigger>
        <TooltipContent>Apple first-party apps are covered by SOFA OS vulnerability feed.</TooltipContent>
      </Tooltip>
    );
  }
  if (!matchable) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  if (scanning) {
    const loaded = progress?.loaded ?? 0;
    const total = progress?.total ?? 0;
    const pct = total > 0 ? Math.min(100, Math.round((loaded / total) * 100)) : 0;
    // If we already have partial CVE hits, surface the current highest severity alongside progress.
    const partialSev = match && match.cves.length > 0 ? match.highestSeverity : null;
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex flex-col items-center gap-1 mx-auto w-24">
            <div className="flex items-center justify-between w-full text-[10px] text-muted-foreground tabular-nums">
              <span>Scanning</span>
              <span>
                {loaded}/{total}
              </span>
            </div>
            <div className="h-1 w-full rounded-full bg-muted overflow-hidden">
              <div
                className={`h-full transition-all ${
                  partialSev === "CRITICAL" || partialSev === "HIGH"
                    ? "bg-destructive"
                    : partialSev === "MEDIUM"
                      ? "bg-warning"
                      : "bg-primary"
                }`}
                style={{ width: `${pct}%` }}
              />
            </div>
            {partialSev && (
              <Badge className={`${SEVERITY_BADGE[partialSev]} text-[9px] px-1 py-0`}>
                {partialSev} · {match!.cves.length}
              </Badge>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent>
          Evaluated {loaded} of {total} version{total === 1 ? "" : "s"} against NVD.
        </TooltipContent>
      </Tooltip>
    );
  }
  if (!match || match.cves.length === 0) {
    return (
      <Badge variant="outline" className="text-[10px] text-success">
        Clean
      </Badge>
    );
  }
  return (
    <Badge className={`${SEVERITY_BADGE[match.highestSeverity]} text-[10px]`}>
      {match.highestSeverity} · {match.cves.length}
    </Badge>
  );
}
